import type { Control } from 'react-hook-form'
import type { VoucherFormValues } from './voucher-form-schema'
import type { VoucherType } from '@/entities/voucher'
import type { AllFilterValue } from '@/shared/constants/filters'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ALL_FILTER_VALUE } from '@/shared/constants/filters'
import { DataTableFilterField } from '@/shared/ui/data-table'
import { FilterPopover } from '@/shared/ui/filter-popover'
import { FormField } from '@/shared/ui/form-field'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group'
import { SectionHeader } from '@/shared/ui/section-header'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { CheckboxList, CheckboxListSkeleton, InfiniteCheckboxList } from './checkbox-list'

interface Option {
  label: string
  value: string
}

interface SelectableItem {
  id: string
  label: string
}

interface VoucherApplicableSectionProps {
  control: Control<VoucherFormValues>
  selectedType: VoucherType
  applicableError: string | undefined
  languageOptions: Option[]
  effectiveLanguageCode: string
  setSelectedLanguageCode: (value: string) => void
  destinationOptions: Option[]
  tourSearch: string
  setTourSearch: (value: string) => void
  tourDestinationId: string
  setTourDestinationId: (value: string) => void
  tourStatus: 'active' | 'inactive' | AllFilterValue
  setTourStatus: (value: 'active' | 'inactive' | AllFilterValue) => void
  tourActiveFilterCount: number
  resetTourFilters: () => void
  toursLoading: boolean
  tourItems: SelectableItem[]
  poiSearch: string
  setPoiSearch: (value: string) => void
  poiCountryId: string
  setPoiCountryId: (value: string) => void
  poiProvinceId: string
  setPoiProvinceId: (value: string) => void
  poiDestinationId: string
  setPoiDestinationId: (value: string) => void
  poiCategory: string
  setPoiCategory: (value: string) => void
  poiStatus: 'active' | 'inactive' | AllFilterValue
  setPoiStatus: (value: 'active' | 'inactive' | AllFilterValue) => void
  poiActiveFilterCount: number
  resetPoiFilters: () => void
  poisLoading: boolean
  poiItems: SelectableItem[]
  fetchNextPoiPage: () => void
  hasNextPoiPage: boolean
  isFetchingNextPoiPage: boolean
  countryOptions: Option[]
  provinceOptions: Option[]
  categoryOptions: Array<{ label: string; value: string }>
}

export function VoucherApplicableSection({
  control,
  selectedType,
  applicableError,
  languageOptions,
  effectiveLanguageCode,
  setSelectedLanguageCode,
  destinationOptions,
  tourSearch,
  setTourSearch,
  tourDestinationId,
  setTourDestinationId,
  tourStatus,
  setTourStatus,
  tourActiveFilterCount,
  resetTourFilters,
  toursLoading,
  tourItems,
  poiSearch,
  setPoiSearch,
  poiCountryId,
  setPoiCountryId,
  poiProvinceId,
  setPoiProvinceId,
  poiDestinationId,
  setPoiDestinationId,
  poiCategory,
  setPoiCategory,
  poiStatus,
  setPoiStatus,
  poiActiveFilterCount,
  resetPoiFilters,
  poisLoading,
  poiItems,
  fetchNextPoiPage,
  hasNextPoiPage,
  isFetchingNextPoiPage,
  countryOptions,
  provinceOptions,
  categoryOptions,
}: VoucherApplicableSectionProps) {
  const { t } = useTranslation()
  const getErrorText = (message: string | undefined) =>
    message ? t(message, { defaultValue: message }) : undefined

  return (
    <div>
      <SectionHeader
        title={t('voucher.sectionApplicable')}
        description={t('voucher.sectionApplicableSubtitle')}
      />
      <div className="space-y-4">
        <FormField label={t('common.type')} required>
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <RadioGroup
                value={field.value}
                onValueChange={(v: VoucherType) => field.onChange(v)}
                className="flex gap-6"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="TOUR" id="type-tour" />
                  <Label htmlFor="type-tour">{t('voucher.type.tour')}</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="POI" id="type-poi" />
                  <Label htmlFor="type-poi">{t('voucher.type.poi')}</Label>
                </div>
              </RadioGroup>
            )}
          />
        </FormField>

        {selectedType === 'TOUR' && (
          <FormField
            label={t('voucher.applicableTours')}
            required
            error={getErrorText(applicableError)}
            hint={t('voucher.applicableHint')}
          >
            <div className="mb-2 flex gap-2">
              <Input
                placeholder={t('common.search')}
                value={tourSearch}
                onChange={(e) => setTourSearch(e.target.value)}
                className="flex-1"
              />
              <FilterPopover
                title={t('common.filter')}
                resetLabel={t('common.reset')}
                activeCount={tourActiveFilterCount}
                onReset={resetTourFilters}
              >
                {languageOptions.length > 1 && (
                  <DataTableFilterField label={t('common.language')}>
                    <Select value={effectiveLanguageCode} onValueChange={setSelectedLanguageCode}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {languageOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </DataTableFilterField>
                )}

                <DataTableFilterField label={t('poi.destination')}>
                  <Select
                    value={tourDestinationId || ALL_FILTER_VALUE}
                    onValueChange={(v) => setTourDestinationId(v === ALL_FILTER_VALUE ? '' : v)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('poi.destination')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_FILTER_VALUE}>{t('common.all')}</SelectItem>
                      {destinationOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </DataTableFilterField>

                <DataTableFilterField label={t('common.status')}>
                  <Select
                    value={tourStatus}
                    onValueChange={(v) => setTourStatus(v as typeof tourStatus)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('common.status')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_FILTER_VALUE}>{t('common.all')}</SelectItem>
                      <SelectItem value="active">{t('poi.activity.active')}</SelectItem>
                      <SelectItem value="inactive">{t('poi.activity.inactive')}</SelectItem>
                    </SelectContent>
                  </Select>
                </DataTableFilterField>
              </FilterPopover>
            </div>
            {toursLoading ? (
              <CheckboxListSkeleton />
            ) : (
              <Controller
                name="applicableTourIds"
                control={control}
                render={({ field }) => (
                  <CheckboxList
                    items={tourItems}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('voucher.noToursAvailable')}
                  />
                )}
              />
            )}
          </FormField>
        )}

        {selectedType === 'POI' && (
          <FormField
            label={t('voucher.applicablePois')}
            required
            error={getErrorText(applicableError)}
            hint={t('voucher.applicableHint')}
          >
            <div className="mb-2 flex gap-2">
              <Input
                placeholder={t('common.search')}
                value={poiSearch}
                onChange={(e) => setPoiSearch(e.target.value)}
                className="flex-1"
              />
              <FilterPopover
                title={t('common.filter')}
                resetLabel={t('common.reset')}
                activeCount={poiActiveFilterCount}
                onReset={resetPoiFilters}
              >
                {languageOptions.length > 1 && (
                  <DataTableFilterField label={t('common.language')}>
                    <Select value={effectiveLanguageCode} onValueChange={setSelectedLanguageCode}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {languageOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </DataTableFilterField>
                )}

                <DataTableFilterField label={t('destination.country')}>
                  <Select
                    value={poiCountryId || ALL_FILTER_VALUE}
                    onValueChange={(v) => {
                      setPoiCountryId(v === ALL_FILTER_VALUE ? '' : v)
                      setPoiProvinceId('')
                      setPoiDestinationId('')
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('destination.country')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_FILTER_VALUE}>{t('common.all')}</SelectItem>
                      {countryOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </DataTableFilterField>

                <DataTableFilterField label={t('destination.province')}>
                  <Select
                    value={poiProvinceId || ALL_FILTER_VALUE}
                    onValueChange={(v) => {
                      setPoiProvinceId(v === ALL_FILTER_VALUE ? '' : v)
                      setPoiDestinationId('')
                    }}
                    disabled={!poiCountryId}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('destination.province')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_FILTER_VALUE}>{t('common.all')}</SelectItem>
                      {provinceOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </DataTableFilterField>

                <DataTableFilterField label={t('poi.destination')}>
                  <Select
                    value={poiDestinationId || ALL_FILTER_VALUE}
                    onValueChange={(v) => setPoiDestinationId(v === ALL_FILTER_VALUE ? '' : v)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('poi.destination')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_FILTER_VALUE}>{t('common.all')}</SelectItem>
                      {destinationOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </DataTableFilterField>

                <DataTableFilterField label={t('poi.category')}>
                  <Select
                    value={poiCategory || ALL_FILTER_VALUE}
                    onValueChange={(v) => setPoiCategory(v === ALL_FILTER_VALUE ? '' : v)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('poi.category')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_FILTER_VALUE}>{t('common.all')}</SelectItem>
                      {categoryOptions.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </DataTableFilterField>

                <DataTableFilterField label={t('common.status')}>
                  <Select
                    value={poiStatus}
                    onValueChange={(v) => setPoiStatus(v as typeof poiStatus)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('common.status')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_FILTER_VALUE}>{t('common.all')}</SelectItem>
                      <SelectItem value="active">{t('poi.activity.active')}</SelectItem>
                      <SelectItem value="inactive">{t('poi.activity.inactive')}</SelectItem>
                    </SelectContent>
                  </Select>
                </DataTableFilterField>
              </FilterPopover>
            </div>
            {poisLoading ? (
              <CheckboxListSkeleton />
            ) : (
              <Controller
                name="applicablePoiIds"
                control={control}
                render={({ field }) => (
                  <InfiniteCheckboxList
                    items={poiItems}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('voucher.noPoisAvailable')}
                    fetchNextPage={fetchNextPoiPage}
                    hasNextPage={hasNextPoiPage}
                    isFetchingNextPage={isFetchingNextPoiPage}
                  />
                )}
              />
            )}
          </FormField>
        )}
      </div>
    </div>
  )
}
